import { AxiosResponse } from 'axios';
import { useCallback } from 'react';
import { ApiUrlMobile } from '@/mobile/utils/constants';
import { getSigV4HeadersMobileForGet } from '@/utils/cognito';
import { QuitInfoGetResponse } from '@/mobile/types/api/mypage/mobileQuitInfo';
import useCommonApiErrorHandling from '@/hooks/useCommonApiErrorHandling';
import useInsertServiceIdInApiUrl from '@/hooks/useInsertServiceIdInApiUrl';
import apiMobile from '@/mobile/utils/apiMobile';
import useSetLoading from '@/hooks/useSetLoading';
import { QuitInfoState } from '@/mobile/states/mobileQuitInfoState';
import { useSetRecoilState } from 'recoil';

const apiUrl = ApiUrlMobile.Read.QUIT_INFO;

/**
 * 解約情報取得用カスタムフック
 * API呼び出し・ローディング制御・エラーハンドリングを共通化し、
 * 呼び出し元では execute() を呼ぶだけで取得処理が完結する。
 *
 * @returns execute - API取得処理を実行する関数
 */
const useGetQuitInfo = () => {
  const { startLoading, finishLoading } = useSetLoading();
  const { insertServiceIdInApiUrl } = useInsertServiceIdInApiUrl();
  const { commonErrorHandling, commonResponseCodeHandling } = useCommonApiErrorHandling();
  const setQuitInfo = useSetRecoilState(QuitInfoState);

  const execute = useCallback(async () => {
    const apiUrlWithServiceId = insertServiceIdInApiUrl(apiUrl);

    // 解約可否は契約回線ごとに異なるため、サービスIDを差し込んだ URL で署名する。
    const headers = await getSigV4HeadersMobileForGet(apiUrlWithServiceId);
    startLoading(apiUrlWithServiceId);

    await apiMobile
      .get(apiUrlWithServiceId, headers)
      .then(({ data }: AxiosResponse<QuitInfoGetResponse>) => {
        const { isError } = commonResponseCodeHandling({
          responseCode: data.response_code,
          isTransitionTop: true,
        });

        // 共通エラーでトップ遷移するケースでは、画面側に解約情報を残さない。
        if (isError) return;

        setQuitInfo(data);
      })
      .catch((error: any) => {
        commonErrorHandling({ error, isTransitionTop: true });
      })
      .finally(() => finishLoading(apiUrlWithServiceId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { execute };
};

export default useGetQuitInfo;
